import { fontFamilyBodyStack, layoutGutterSmall, layoutMarginSmall } from '@issuu/silkscreen';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import type { AppProps, ReaderViewState, SendMessage } from '../../types';
import { shouldShowAds } from '../../util/ads-utils';
import { assertNever } from '../../util/assertNever';
import { AdsInterstitial } from '../Ads';
import { WIDTH_PRIMIS_AD, WIDTH_PRIMIS_AD_WIDE } from '../Ads/AdsPrimis';
import { AndroidInstallPrompt } from '../AndroidInstallPrompt';
import { ContentWarning } from '../ContentWarning';
import { DocumentInfo } from '../DocumentInfo';
import { MobileCoverBrief } from '../MobileCoverBrief';
import { MobileLeadStory } from '../MobileLeadStory';
import { ReaderWrapper } from '../ReaderWrapper';
import { SkipNavButton } from '../ScreenReaderOnly';
import { SeeMore } from '../SeeMore';
import { StreamShelf } from '../StreamShelf';
import { VisitorCTA } from '../VisitorCTA';
import type { DocumentData } from './DocumentContext';
import DocumentContext from './DocumentContext';
import { Explore } from './Explore';
import { LeftSidebar } from './LeftSidebar';
import type { GridArea } from './useGridLayout';
import { getLayoutStyles, useLayout } from './useGridLayout';

const fontOverride = css`
    button,
    label {
        font-family: ${fontFamilyBodyStack};
    }
`;

const Root = styled.div<{ wideReader: boolean }>`
    position: relative;
    padding: ${layoutMarginSmall};
    width: 100%;
    margin: auto;
    font-family: ${fontFamilyBodyStack};
    display: grid;
    justify-items: stretch;
    gap: ${layoutGutterSmall};
    ${props => getLayoutStyles(props)}
    ${fontOverride}

    > * {
        // To allow content-holding grid items to shrink to fit their assigned grid area
        // See also: https://css-tricks.com/preventing-a-grid-blowout/
        min-width: 0;
    }
`;

const VerticalWrapper = styled.div<{ gridArea: GridArea; width?: number }>`
    ${props => (props.width ? `width: ${props.width}px;` : '')}
    grid-area: ${({ gridArea }) => gridArea};
    display: flex;
    flex-direction: column;
`;

const AreaComponents: React.FC = () => {
    const {
        appState: {
            currentLayout: { areas, name: layout },
        },
    } = useContext(DocumentContext);
    const columnWidth =
        layout === 'twoColumn'
            ? WIDTH_PRIMIS_AD + 2 * parseInt(layoutMarginSmall, 10)
            : layout === 'wideScreen'
            ? WIDTH_PRIMIS_AD_WIDE + 2 * parseInt(layoutMarginSmall, 10)
            : 0;
    return (
        <>
            {[...areas].map(area => {
                switch (area) {
                    case 'cover-brief':
                        return <MobileCoverBrief key={area} />;
                    case 'lead-story':
                        return <MobileLeadStory key={area} />;
                    case 'reader':
                        return <ReaderWrapper key={area} />;
                    case 'document-info':
                        return <DocumentInfo key={area} />;
                    case 'see-more':
                        return (
                            <VerticalWrapper gridArea={area} key={area} width={columnWidth}>
                                <SeeMore />
                                <VisitorCTA />
                            </VerticalWrapper>
                        );
                    case 'explore':
                        return <Explore key={area} />;
                    case 'shelf-read-more':
                        return <StreamShelf key={area} streamName="read-more" />;
                    case 'shelves':
                        return (
                            <VerticalWrapper gridArea={area} key={area}>
                                <StreamShelf streamName="similar-to" />
                                <StreamShelf streamName="popular-now" />
                                <StreamShelf streamName="just-for-you" />
                            </VerticalWrapper>
                        );
                    default:
                        assertNever(area);
                        return null;
                }
            })}
        </>
    );
};

const DocumentPageContent: React.FC<Record<string, never>> = () => {
    const context = useContext(DocumentContext);
    const {
        appState: {
            readerViewState: [readerViewState],
        },
        document: { contentRating },
    } = context;

    const [allowWarning, setAllowWarning] = useState<boolean>(true);
    const isExplicit = contentRating.isReviewed && contentRating.isExplicit;
    const showWarning = allowWarning && (!contentRating.isReviewed || isExplicit);

    return (
        <>
            <AndroidInstallPrompt />
            <SkipNavButton />
            {showWarning ? (
                <ContentWarning onClose={() => isExplicit && setAllowWarning(false)} />
            ) : (
                <Root
                    itemScope={true}
                    itemType="http://schema.org/CreativeWork"
                    wideReader={readerViewState === 'large'}
                >
                    <LeftSidebar />
                    <AreaComponents />
                </Root>
            )}
            {shouldShowAds({ context }) ? <AdsInterstitial /> : null}
        </>
    );
};

type ProviderProps = AppProps & {
    children: JSX.Element;
};

export const DocumentProvider: React.FC<ProviderProps> = ({
    initialDocumentData,
    initialPageNumber,
    initialViewportWidth,
    config,
    isCrawler,
    pageTexts,
    referrer,
    websiteTracker,
    monitorTracker,
    storyTracker,
    googleAnalyticsTracker,
    children,
}: ProviderProps) => {
    useEffect(() => {
        websiteTracker({
            type: 'webpage_load',
        });
        googleAnalyticsTracker('send', { hitType: 'pageview' });
    }, []);

    const currentPageNumbers = useState([initialPageNumber || 1]);
    const readerViewState = useState<ReaderViewState>(
        initialDocumentData.publisher.licenses.defaultLargeViewReader ? 'large' : 'small',
    );
    const currentLayout = useLayout({
        viewportWidth: initialViewportWidth,
        wideReader: readerViewState[0] === 'large',
    });

    const context: DocumentData = {
        ...initialDocumentData,
        hosts: config.hosts,
        crawler: isCrawler && { pageNumber: initialPageNumber, pageTexts },
        enviroment: config.sentry.environment,
        referrer,
        trackers: {
            monitor: monitorTracker,
            story: storyTracker,
            website: websiteTracker,
            googleAnalytics: googleAnalyticsTracker,
        },
        appState: {
            sendMessageToReader: useRef<SendMessage>(),
            readerRef: useRef<HTMLDivElement | null>(null),
            currentLayout,
            currentPageNumbers,
            readerViewState,
        },
    };
    return <DocumentContext.Provider value={context}>{children}</DocumentContext.Provider>;
};

export const DocumentPage: React.FC<AppProps> = (props: AppProps) => (
    <DocumentProvider {...props}>
        <DocumentPageContent />
    </DocumentProvider>
);
